import { ReactNode } from "react";
import { Swiper, SwiperSlide, SwiperSlideProps } from "swiper/react";
import { Navigation, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./Carousel.scss";
import { SectionHeading } from "../Section";
import { ArrowLink } from "../ArrowLink";

type CarouselProps = {
  title?: string;
  children?: Array<ReactNode>;
  type?: "default" | "sectionHome";
  linkHref?: string;
  linkLabel?: string;
  asLink?: React.ElementType;
};

export function Carousel({
  title,
  children = [],
  type = "default",
  linkHref,
  linkLabel = "Scopri tutti",
  asLink = "a",
}: CarouselProps) {
  return (
    <section className="carousel-wrapper" aria-label={title || undefined}>
      <SectionHeading
        title={title}
        color={type === "sectionHome" ? "slate-20" : "primary"}
        className="carousel-title"
        titleMarginBottom="0"
      >
        {linkHref && (
          <ArrowLink
            href={linkHref}
            asLink={asLink}
            fs="7"
            aria-label={title ? `${linkLabel} - ${title}` : linkLabel}
          >
            {linkLabel.toUpperCase()}
          </ArrowLink>
        )}
      </SectionHeading>
      <div className="carousel-outer-wrapper">
        <Swiper
          className="carousel-inner-wrapper"
          spaceBetween={24}
          modules={[Navigation, Pagination, A11y]}
          a11y={{
            prevSlideMessage: "Slide precedente",
            nextSlideMessage: "Slide successiva",
            lastSlideMessage: "",
            firstSlideMessage: "",
          }}
          watchSlidesProgress
          breakpoints={{
            0: {
              slidesPerView: 1,
              spaceBetween: 24,
              centeredSlides: true,
            },
            768: {
              slidesPerView: 2,
              spaceBetween: 24,
              centeredSlides: false,
            },
            992: {
              slidesPerView: 3,
              spaceBetween: 24,
              centeredSlides: false,
            },
          }}
          pagination={
            children.length > 3
              ? {
                  enabled: true,
                  clickable: true,
                }
              : {}
          }
          loop={children.length > 3}
          navigation={children.length > 3}
          onInit={(swiper: any) => {
            swiper.slides.forEach((slide: any) => {
              slide.inert = !slide.classList.contains("swiper-slide-visible");
            });
            swiper.slides
              .slice(
                swiper.activeIndex,
                swiper.activeIndex + Number(swiper.params.slidesPerView),
              )
              .forEach((slide: any, index: number) => {
                slide.classList.remove("lastViewableSlide");
                if ((index + 1) % Number(swiper.params.slidesPerView) === 0) {
                  slide.classList.add("lastViewableSlide");
                }
              });
          }}
          onSlideChange={(swiper: any) => {
            swiper.slides.forEach((slide: any) => {
              slide.inert = !slide.classList.contains("swiper-slide-visible");
            });
            swiper.slides
              .slice(
                swiper.activeIndex,
                swiper.activeIndex + Number(swiper.params.slidesPerView),
              )
              .forEach((slide: any, index: number) => {
                slide.classList.remove("lastViewableSlide");
                if ((index + 1) % Number(swiper.params.slidesPerView) === 0) {
                  slide.classList.add("lastViewableSlide");
                }
              });
          }}
        >
          {children.map((child, index) => (
            <SwiperSlide className="swiper-slide-custom" key={index}>
              {child as SwiperSlideProps["children"]}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}
